/* eslint max-lines-per-function: ["error", {max: 175, skipBlankLines: true, skipComments: true}] -- very large/complex component */
/* eslint max-statements: ["error", {max:30}] -- applies to entire file */
/* eslint complexity: ["error", {max:14}] -- applies to entire file */
import { useEffect, useRef, useState } from "react";

import isNil from "lodash/isNil";

import { ConfirmationAlertTestID, TasksTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useToast } from "@/hooks/useToast";
import { useUnmount } from "@/hooks/useUnmount";
import useAppMonitor from "@/services/AppMonitor";
import { useGetTasksQuery, useLogWeightMutation } from "@/store/api/tasks";
import { useAppSelector } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import {
  selectTaskDetailsData,
  useTaskDetailsModalDispatch,
} from "@/store/reducers/modals/taskDetailsModal";
import TaskBasicDetails from "@/ui/components/tasks/TaskBasicDetails";
import TaskDetailsModalWrapper, {
  FooterActionLabel,
} from "@/ui/components/tasks/TaskDetailsModalWrapper";
import ConfirmationAlertModal from "@/ui/composed/alerts/ConfirmationAlert";
import DateTimePicker from "@/ui/composed/pickers/DateTimePicker";
import { FAIconName, FullScreenSpinner, Text, TextField } from "@/ui/core";
import Accordion from "@/ui/core/Accordion";
import InputLabel from "@/ui/core/InputLabel";
import { HStack, VStack } from "@/ui/layout";
import { combineDateTime } from "@/utils/date.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { getIsWeightInvalid } from "@/utils/user.util";

const handleAlertConfirm = () => {
  router.dismiss();
};

const baseTestID = TasksTestID.TaskDetails;

const Modal = () => {
  const { captureException } = useAppMonitor();
  const { showToast } = useToast();
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector() || [];
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [time, setTime] = useState<Date>(new Date());
  const [weight, setWeight] = useState<string>("");
  const [logWeight, { isLoading }] = useLogWeightMutation();
  const { refetch: refetchTasks } = useGetTasksQuery({
    appUserId,
    isPatient,
    linkedPatients: proxyPatients,
  });
  const renderCountRef = useRef<number>(1);
  useEffect(() => {
    renderCountRef.current = renderCountRef.current + 1;
  });

  const { resetTaskDetailsData } = useTaskDetailsModalDispatch();
  useUnmount({ onUnmount: resetTaskDetailsData });

  const isWeightInvalid = getIsWeightInvalid(weight);

  const taskDetailsData = useAppSelector(selectTaskDetailsData);

  const {
    description,
    duration,
    patientExternalId,
    patientName,
    selectedUserId,
    taskId,
    title,
  } = taskDetailsData || {};

  if (
    isNil(title) ||
    isNil(description) ||
    isNil(duration) ||
    !isNotNilNotEmpty(patientExternalId) ||
    !isNotNilNotEmpty(taskId) ||
    !isNotNilNotEmpty(selectedUserId)
  ) {
    // capture renderCount in error message to enable better debugging and
    // to distinguish this happening on initial render, a true critical error,
    // versus on subsequent render, which means initial render succeeded and something just goes wrong later
    captureException(
      new Error(
        `${title} log-weight modal is missing required params on render #${renderCountRef.current}`,
      ),
    );
    const text =
      "There was an issue getting the details to be able to log weight";
    showToast({ text, variant: "error" });
    // NOTE: this prevents the Android specific error 'addViewAt: failed to insert view [####] into parent [####] at index 1 The specified child already has a parent. You must call removeView) on the child's parent first.'
    router.dismiss();
    return <FullScreenSpinner />;
  }

  const openConfirmationAlert = () => {
    setIsAlertOpen(true);
  };

  const dismissAlert = () => {
    setIsAlertOpen(false);
  };

  const handleWeightInputChange = (currentWeight: string) => {
    setWeight(currentWeight);
  };

  const handleLogWeight = async () => {
    const combinedDate = combineDateTime({ date, time });
    logWeight({
      appUserId,
      isPatient,
      linkedPatients: proxyPatients,
      observationDatetime: combinedDate.toISOString(),
      patientExternalId,
      selectedUserId,
      source: "Manual",
      status: "Final",
      taskId,
      type: "Weight",
      unit: "lbs",
      value: Number(weight),
    })
      .unwrap()
      .then(() => {
        void refetchTasks();
        requestAnimationFrame(() => {
          router.dismiss();
        });

        showToast({
          text: "Weight logged",
          variant: "success",
        });
      })
      .catch(() => {
        showToast({ text: "Failed to log weight", variant: "error" });
      });
  };

  return (
    <TaskDetailsModalWrapper
      footerRightButtonLabel={FooterActionLabel.LogWeight}
      isLoading={isLoading}
      isRightButtonDisabled={isWeightInvalid || isLoading}
      onFooterLeftButtonPress={
        isWeightInvalid ? undefined : openConfirmationAlert
      }
      onFooterRightButtonPress={handleLogWeight}
    >
      <VStack gap={24}>
        <TaskBasicDetails
          duration={duration}
          patientName={patientName}
          testID={baseTestID}
          title={title}
        />
        <Accordion testID={baseTestID} title="Directions">
          <Text
            testID={joinTestIDs(baseTestID, "directions")}
            variant="caption"
          >
            {description}
          </Text>
        </Accordion>

        <VStack gap={6}>
          <InputLabel
            testID={joinTestIDs(baseTestID, "weight")}
            text="Weight"
          />
          <HStack alignItems="center" gap={8}>
            <TextField
              accessibilityLabel="Weight input field"
              keyboardType="decimal-pad"
              onChange={handleWeightInputChange}
              placeholder="Please enter"
              testID={joinTestIDs(baseTestID, "weight")}
              value={weight}
            />
            <Text testID={joinTestIDs(baseTestID, "weight-unit")}>lbs</Text>
          </HStack>
        </VStack>
        <VStack gap={6}>
          <InputLabel
            testID={joinTestIDs(baseTestID, "date-time")}
            text="Measured on"
          />
          <HStack alignItems="center" gap={12} justifyContent="space-between">
            <DateTimePicker
              date={date}
              mode="date"
              onChange={setDate}
              testID={joinTestIDs(baseTestID, "date")}
            />
            <DateTimePicker
              date={time}
              mode="time"
              onChange={setTime}
              testID={joinTestIDs(baseTestID, "time")}
            />
          </HStack>
        </VStack>
      </VStack>
      <ConfirmationAlertModal
        confirmationType="destructive"
        confirmLeftIcon={FAIconName.Times}
        confirmText="Discard changes"
        isOpen={isAlertOpen}
        message="If you leave now, any information you’ve entered will be lost and can’t be recovered."
        onConfirm={handleAlertConfirm}
        onDismiss={dismissAlert}
        testID={ConfirmationAlertTestID.CancelLogWeight}
        title="Are you sure?"
      />
    </TaskDetailsModalWrapper>
  );
};

// eslint-disable-next-line import-x/no-unused-modules -- Modal/Tab/TabLayout/Screen/Home get used in expo routing but in a way that TypeScript can not infer
export default Modal;
